import * as React from 'react';
import { ConnectButton } from '@rainbow-me/rainbowkit';
import { useBalance, useReadContracts, useAccount } from 'wagmi';
import { formatEther } from 'viem';
import { multiMint } from '../multiMint';
import { Withdraw } from './Withdraw';
import { OwnedNfts } from './OwnedNfts';
import { Inv } from './multiselect/Inv';
import { Cart } from './multiselect/Cart';

const ZERO_ADDR = '0x0000000000000000000000000000000000000000';
const DEV_ADDR = '0x952440136379bA4cEEf92FF49fb122C4Ace0B810';
const TOTAL_SUPPLY = 100;

export function Home() {
  const { address, isConnected } = useAccount();
  const [cart, setCart] = React.useState([]);
  const [inCartStatus, setInCartStatus] = React.useState({});

  const multimint = React.useMemo(
    () => ({ address: multiMint.addr, abi: multiMint.abi }),
    []
  );

  const queryAddr = address ?? ZERO_ADDR;

  const { data: balData } = useBalance({
    address: multimint.address,
    query: { refetchInterval: 20000 },
  });
  const balance = balData?.formatted ?? '0';

  const {
    data: reads,
    isLoading,
    isError,
  } = useReadContracts({
    allowFailure: true,
    contracts: [
      { ...multimint, functionName: 'getAvailableTokens' }, // 0
      { ...multimint, functionName: 'price' }, // 1
      { ...multimint, functionName: 'tokensOfAddress', args: [queryAddr] }, // 2
      { ...multimint, functionName: 'balanceOf', args: [queryAddr] }, // 3
      { ...multimint, functionName: 'name' }, // 4
      { ...multimint, functionName: 'symbol' }, // 5
      { ...multimint, functionName: 'owner' }, // 6
    ],
    query: { refetchInterval: 20000 },
  });

  // Defensive extraction — any failed read returns { status: 'failure' }
  const availableRaw =
    reads?.[0]?.status === 'success' ? reads[0].result : null;
  const priceRaw = reads?.[1]?.status === 'success' ? reads[1].result : null;
  const ownedRaw = reads?.[2]?.status === 'success' ? reads[2].result : null;
  const collectionName =
    reads?.[4]?.status === 'success' ? reads[4].result : '';
  const contractOwnerAddr =
    reads?.[6]?.status === 'success' ? reads[6].result : null;

  const ids = React.useMemo(() => {
    if (!Array.isArray(availableRaw)) return [];
    return [...availableRaw].map((n) => Number(n)).sort((a, b) => a - b);
  }, [availableRaw]);

  const allIds = React.useMemo(
    () => Array.from({ length: TOTAL_SUPPLY }, (_, i) => i + 1),
    []
  );

  const soldIds = React.useMemo(
    () => allIds.filter((id) => !ids.includes(id)),
    [allIds, ids]
  );

  const sold = React.useMemo(() => {
    const out = {};
    for (const id of allIds) out[id] = !ids.includes(id);
    return out;
  }, [allIds, ids]);

  const bigPrice = typeof priceRaw === 'bigint' ? priceRaw : 0n;
  let eth = '0';
  try {
    eth = formatEther(bigPrice);
  } catch (_e) {
    eth = '0';
  }

  const ownedNfts = React.useMemo(() => {
    if (!Array.isArray(ownedRaw)) return [];
    return ownedRaw.map((n) => Number(n));
  }, [ownedRaw]);

  const isContractOwner = React.useMemo(() => {
    if (!address) return false;
    const a = address.toLowerCase();
    if (a === DEV_ADDR.toLowerCase()) return true;
    if (
      contractOwnerAddr &&
      typeof contractOwnerAddr === 'string' &&
      a === contractOwnerAddr.toLowerCase()
    )
      return true;
    return false;
  }, [address, contractOwnerAddr]);

  function addToCart(id) {
    setCart((prev) => [...prev, id]);
    setInCartStatus((prev) => ({ ...prev, [id]: true }));
  }

  function removeFromCart(id) {
    setCart((prev) => prev.filter((item) => item !== id));
    setInCartStatus((prev) => ({ ...prev, [id]: false }));
  }

  function resetCart() {
    setCart([]);
    setInCartStatus({});
  }

  // no-op kept for backward compatibility with NftCard / Inv
  const setSold = React.useCallback(() => {}, []);

  const ownedNftsRef = React.useRef(null);
  function scrollOwnedNfts() {
    ownedNftsRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }

  if (isLoading && !reads) {
    return (
      <>
        <div className='flex justify-center'>
          <ConnectButton />
        </div>
        <div>loading...</div>
      </>
    );
  }

  // Even on read error, render UI so users can still connect / retry.
  return (
    <>
      {cart.length === 0 && (
        <div className='flex justify-center '>
          <ConnectButton />
        </div>
      )}

      {isError && (
        <div style={{ color: '#0178db', padding: '8px' }}>
          Network read failed — retrying in the background. Connect your wallet
          to continue.
        </div>
      )}

      {isContractOwner ? (
        <Withdraw bal={balance} addr={multimint.address} />
      ) : null}

      {cart.length > 0 ? (
        <Cart
          ids={ids}
          cart={cart}
          removeFromCart={removeFromCart}
          bigPrice={bigPrice}
          eth={eth}
          inCartStatus={inCartStatus}
          resetCart={resetCart}
          sold={sold}
          setSold={setSold}
        />
      ) : null}

      {ids.length > 0 ? (
        <Inv
          ids={allIds}
          addToCart={addToCart}
          removeFromCart={removeFromCart}
          resetCart={resetCart}
          bigPrice={bigPrice}
          eth={eth}
          inCartStatus={inCartStatus}
          sold={sold}
          setSold={setSold}
          soldIds={soldIds}
          scrollOwnedNfts={scrollOwnedNfts}
        />
      ) : null}

      <div ref={ownedNftsRef}>
        {isConnected && ownedNfts.length > 0 ? (
          <OwnedNfts ids={ownedNfts} collectionName={collectionName} />
        ) : isConnected ? (
          <div>
            <div>
              <br></br>
            </div>
            <span style={{ color: '#0178db', fontSize: '1.6em' }}>
              THE CONNECTED ADDRESS OWNS NO NFTS YET FROM THIS COLLECTION
            </span>
            <div>
              <br></br>
              <br></br>
            </div>
          </div>
        ) : null}
      </div>
    </>
  );
}
